/**
 * Resignation letter formats — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_office_formats`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const resignationLetterFormatsPack: ToilePack = {
    slug: 'resignation-letter-formats',
    category: 'office',
    name: 'Resignation letter formats',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'standard', label: 'Standard', subject: 'a resignation letter format with date, manager, subject "Resignation" and notice period labelled'},
        {key: 'short', label: 'Short notice', subject: 'a resignation letter format with a brief body'},
        {key: 'email', label: 'Email', subject: 'a resignation email format'},
    ],
    styles: [
        {key: 'plain', label: 'Plain', style: 'plain business letter typesetting in a black serif, left-aligned blocks, wide line spacing', palette: 'black ink on white', framing: 'single page seen straight on, centered on a plain light grey ground with an even white margin around the sheet'},
        {key: 'rule', label: 'Header rule', style: 'business letter with a thin navy rule under a company name line and a matching footer rule', palette: 'navy and grey on white', framing: 'single page seen straight on, centered on a plain light grey ground with an even white margin around the sheet'},
        {key: 'warm', label: 'Warm', style: 'friendly letter on warm off-white paper with a small olive accent bar at the top', palette: 'olive and dark grey on off-white', framing: 'single page seen straight on, centered on a plain light grey ground with an even white margin around the sheet'},
    ],
    directions: ['blue rule', 'handwritten', 'with logo'],
    directionHint: 'try: blue rule',
    seo: {
        title: 'Resignation letter format: standard, short, email',
        metaDescription: 'Nine printable resignation letter formats: a standard letter, a short-notice letter and an email, each in a plain, header rule or warm style.',
        h1: 'Resignation letter format: standard, short notice, email',
        tagline: 'Three resignation layouts, each in three styles, with the date and subject lines marked.',
        intro: 'A resignation letter format is the layout for telling an employer you are leaving. These nine show a standard letter with a date, a manager line, a subject and a notice period; a short-notice letter; and an email resignation. Only the headings are lettered; grey lines mark your own words. Type a twist, such as blue rule, and it is a request.',
        useCases: [
            'giving notice at a job',
            'a short notice for a role with a set notice period',
            'a resignation sent by email',
            'a copy to keep for your records',
        ],
        faq: [
            {q: 'What goes in the standard resignation letter?', a: "A date, the manager's name, a subject line reading Resignation, and a labelled notice period. The body is grey lines for your own words."},
            {q: 'How is the email format different?', a: 'It is laid out as a message for sending by email, with a subject line and a short body, instead of a printed letter.'},
            {q: 'Is the body text written for me?', a: 'No. Only the headings are lettered. The body is grey placeholder lines, so you write your own wording.'},
            {q: 'Can I add a logo or a handwritten look?', a: 'Type it as a twist, such as with logo or handwritten. It is a request for that look, not a promised result.'},
        ],
    },
}
